import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError, from, forkJoin, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { Business } from './business.model';
import { environment } from '../../../environments/environment';
import { Page } from '../../models/page-wrapper.model';
import { PickerRecentCacheService } from '../../shared/services/picker-recent-cache.service';
import { EntityDropdownPickerAdapter } from '../../shared/components/entity-dropdown-picker/entity-dropdown-picker.model';
import { PickerRecentRecord } from '../../shared/components/entity-dropdown-picker/picker-recent.model';

@Injectable({
  providedIn: 'root'
})
export class BusinessService {
  private apiUrl = `${environment.restApiUrl}masterdata/businesses`;
  private readonly pickerEntityKey = 'business';

  constructor(
    private http: HttpClient,
    private recentPickerCache: PickerRecentCacheService
  ) { }

  private handleError(error: HttpErrorResponse) {
    // A client-side or network error occurred.
    if (error.error instanceof ErrorEvent) {
      console.error('An error occurred:', error.error.message);
    } else {
      // The backend returned an unsuccessful response code.
      console.error(
        `Backend returned code ${error.status}, ` +
        `body was: ${JSON.stringify(error.error)}`
      );
    }
    // Return an observable with a user-facing error message.
    return throwError(() => new Error('Something went wrong; please try again later.'));
  }

  // getAllBusinesss(params?: any): Observable<Page<Business>> {
  //   return this.http.get<Page<Business>>(this.apiUrl, { params }).pipe(catchError(this.handleError));
  // }
  
  getAllBusinesss(params?: any): Observable<Page<Business>> {
    return this.http.get<Page<Business>>(this.apiUrl + '/search', { params:params }).pipe(catchError(this.handleError));
  }

  getBusinessById(id: number): Observable<Business> {
    return this.http.get<Business>(`${this.apiUrl}/${id}`).pipe(catchError(this.handleError));
  }

  searchBusinesses(query: string, page = 0, size = 25): Observable<Page<Business>> {
    return forkJoin([
      this.searchBusinessesByField('businessName', query, page, size),
      this.searchBusinessesByField('businessCode', query, page, size),
    ]).pipe(
      map(([namePage, codePage]) => this.mergeBusinessPages(namePage, codePage, page, size)),
      catchError(this.handleError)
    );
  }

  loadRecentBusinesses(limit = 10): Observable<Business[]> {
    return from(this.recentPickerCache.loadRecent<Business>(this.pickerEntityKey, limit));
  }

  getFreshBusinessById(id: number | string): Observable<Business> {
    return this.getBusinessById(Number(id));
  }

  rememberBusiness(business: Business): Observable<void> {
    const record: PickerRecentRecord<Business> = {
      key: `${this.pickerEntityKey}::${business.id}`,
      entityKey: this.pickerEntityKey,
      userKey: this.getCurrentUserKey(),
      entityId: business.id,
      label: `${business.businessName ?? ''}${business.businessCode ? ` - ${business.businessCode}` : ''}`.trim(),
      payload: business,
      lastUsedAt: Date.now(),
    };

    return from(this.recentPickerCache.remember(record));
  }

  createBusinessPickerAdapter(): EntityDropdownPickerAdapter<Business> {
    return {
      entityKey: this.pickerEntityKey,
      loadRecent: (limit?: number) => this.loadRecentBusinesses(limit),
      search: (query: string, page: number, size: number) => this.searchBusinesses(query, page, size),
      getFreshById: (id: number | string) => this.getFreshBusinessById(id),
      rememberSelection: (business: Business) => this.rememberBusiness(business),
      getId: (business: Business) => business.id,
      getLabel: (business: Business) => `${business.businessName ?? business.businessCode ?? business.id ?? ''}${business.businessCode && business.businessName ? ` - ${business.businessCode}` : ''}`.trim(),
      getSecondaryLabel: () => '',
      filterBy: 'businessName,businessCode',
    };
  }

  createBusiness(brand: Partial<Business>): Observable<Business> {
    return this.http.post<Business>(this.apiUrl, brand).pipe(catchError(this.handleError));
  }

  updateBusiness(brand: Partial<Business>): Observable<Business> {
    return this.http
      .put<Business>(`${this.apiUrl}/${brand.id}`, brand)
      .pipe(catchError(this.handleError));
  }

  deleteBusiness(id: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/${id}`).pipe(catchError(this.handleError));
  }

  private getCurrentUserKey(): string {
    try {
      const rawUser = sessionStorage.getItem('appUser');
      if (!rawUser) {
        return 'anonymous';
      }

      const parsed = JSON.parse(rawUser);
      return String(parsed?.id ?? 'anonymous');
    } catch {
      return 'anonymous';
    }
  }

  private searchBusinessesByField(
    field: 'businessName' | 'businessCode',
    query: string,
    page: number,
    size: number
  ): Observable<Page<Business>> {
    const params = {
      [field]: query,
      page,
      size,
    };

    return this.http.get<Page<Business>>(`${this.apiUrl}/search`, { params }).pipe(
      catchError(() => of(this.emptyBusinessPage(page, size)))
    );
  }

  private mergeBusinessPages(
    namePage: Page<Business>,
    codePage: Page<Business>,
    page: number,
    size: number
  ): Page<Business> {
    const merged = new Map<number, Business>();
    [...(namePage.content || []), ...(codePage.content || [])].forEach((business) => {
      if (business?.id !== undefined && business?.id !== null) {
        merged.set(business.id, business);
      }
    });

    const content = Array.from(merged.values()).slice(0, size);

    return {
      ...namePage,
      content,
      number: page,
      size,
      totalElements: merged.size,
      numberOfElements: content.length,
    };
  }

  private emptyBusinessPage(page: number, size: number): Page<Business> {
    return {
      content: [],
      pageable: {
        pageNumber: page,
        pageSize: size,
        sort: { sorted: false, unsorted: true, empty: true },
        offset: page * size,
        paged: true,
        unpaged: false,
      },
      last: true,
      totalPages: 0,
      totalElements: 0,
      size,
      number: page,
      sort: { sorted: false, unsorted: true, empty: true },
      first: page === 0,
      numberOfElements: 0,
      empty: true,
    };
  }
}
